import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    autoSearchSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateurApiObject,
    groupeTravailActiveInscriptionsSelector,
} from '@europrocurement/l2d-domain';

import {
    GroupesDeTravailJsonld as GroupeTravailDTO,
    JointureUtilisateurGroupeTravailJsonldUserworkgroupRead as Inscriptions,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { SearchBar, CenterCircularProgress, useModal } from '@europrocurement/flexy-components';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Divider, Typography } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { AppDispatch } from '../../../../../redux';
import { formatNewInscriptionDTO } from '../../../functions/formatInscriptionsDTO';
import { SearchMembersWidget } from './ModalWidget/SearchMembersWidget';
import { EditMembersWidget } from './ModalWidget/EditMembersWidget';

/**
 * Types Declaration part.
 */
// Workgroup Update Form props.
type Props = {
    workgroup: GroupeTravailUtilisateurApiObject;
    historicInscription: boolean;
    isActiveStatus: boolean;
    members: Inscriptions[];
};

/**
 * Workgroup Update infos form
 *
 * Lot of Async here! Be careful while modifying...
 */
export const WorkGroupUpdateMembersForm: React.FunctionComponent<Props> = function ({
    workgroup,
    historicInscription,
    isActiveStatus,
    members,
}) {
    // Standard hook
    const dispatch = useDispatch<AppDispatch>();
    const theme = useTheme();
    const { modalActions } = useModal();

    // Selectors
    const searchDataSource = useUtilisateursServiceSelector(autoSearchSelector);

    // Services
    const { getAutoSearch, selectGroupeTravail, groupesTravailApi } = useUtilisateurService();

    // SearchBar state that will determine multifieldsearch value
    const [searchBarValue, setSearchBarValue] = useState<string>('');
    const [searchReady, setSearchReady] = useState<boolean>(false);

    // Other States
    const [selectedCandidate, setSelectedCandidate] = useState<UtilisateurApiObject | null>(null);

    // Apply standard filters parameters on mount. They should not be modified.
    useEffect(() => {
        // Reset/clean search parameters.
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Pagination`,
            payload: {},
        });
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Filter`,
            payload: {},
        });
        dispatch({
            type: `${searchDataSource.slicename}/reset${searchDataSource.name}Search`,
            payload: {},
        });

        // Set filters to fetch active only inscriptions full list
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Pagination`,
            payload: { page: 0, itemsPerPage: 5 },
        });
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Filter`,
            payload: { key: 'isDeleted', value: false },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dispatch the search value with delay
    const delayedSearch = useDebouncedCallback((searchValue: string) => {
        // Must use search as argument here or it will NOT be recognized by store.
        dispatch({
            type: `${searchDataSource.slicename}/set${searchDataSource.name}Search`,
            payload: { search: searchValue }, // SET SEARCH STATE VALUE HERE
        });
    }, 1000);

    /**
     * Check if searchBarValue is the same that dataSource.search
     * Needed to avoid async issues before calling getAutoSearch.
     */
    useEffect(() => {
        if (searchDataSource.search !== '' && searchDataSource.search === searchBarValue) {
            setSearchReady(true);
        } else if (searchReady !== false) {
            setSearchReady(false);
            // reset store data to avoid showing old search items
            dispatch({
                type: `${searchDataSource.slicename}/clear${searchDataSource.name}Data`,
                payload: {},
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchDataSource.search, searchBarValue]);

    // Launch search if conditions are met.
    useEffect(() => {
        if (searchReady) {
            dispatch(getAutoSearch({}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchReady]);

    /**
     * Fetch Inscriptions part.
     */
    const inscriptionsDataSource = useUtilisateursServiceSelector(
        groupeTravailActiveInscriptionsSelector,
    );
    const [inscriptions, setInscriptions] = useState<Inscriptions[]>([]);

    useEffect(() => {
        if (inscriptionsDataSource.data) {
            setInscriptions(inscriptionsDataSource.data);
        }
    }, [inscriptionsDataSource]);

    // Avoid to show workgroup active members
    const checkCandidatesId = (candidateId: number): boolean => {
        let mustBeShown: boolean = true;

        for (let i = 0; i < inscriptions.length; i++) {
            if (inscriptions[i].user?.id === candidateId || selectedCandidate?.id === candidateId) {
                mustBeShown = false;
            }
        }
        return mustBeShown;
    };

    /**
     * Workgroup update function.
     *
     * Submitted on validation button click.
     */
    const updateMembers = () => {
        // Check to not allow modification on a deleted workgroup
        if (workgroup.fDelete !== false) {
            enqueueSnackbar('Ce groupe est inactif et ne peut donc pas être modifié', {
                variant: 'error',
            });
            return;
        }
        // Handle no selection and inscriptions fetch issues.
        if (selectedCandidate === null) {
            enqueueSnackbar('Aucun utilisateur sélectionné', { variant: 'error' });
            return;
        }

        const formatedNewInscription = formatNewInscriptionDTO(selectedCandidate, workgroup);

        // Format workgroup DTO.
        const updatedWorkgroup: GroupeTravailDTO = {
            idGroupeTravail: workgroup['@id'],
            lib: workgroup.lib,
            domaine: workgroup.domaine?.['@id'],
            iduserReferent: workgroup.iduserReferent?.['@id'],
            fDelete: workgroup.fDelete,
            phone: workgroup.phone,
            email: workgroup.email,
            dCreation: workgroup.dCreation,
            dCloture: workgroup.dCloture,
            inscriptions: [formatedNewInscription],
        };

        const updateWorkGroup = () => {
            groupesTravailApi
                .apiGroupestravailIdGroupeTravailStatusPatch({
                    idGroupeTravail: `${workgroup.id}`,
                    status: 'none',
                    groupesDeTravail: updatedWorkgroup,
                })
                .then(() => {
                    // Close form modal.
                    modalActions.close();

                    // Add notification to user.
                    enqueueSnackbar("L'utilisateur a bien été inscrit au groupe de travail.", {
                        variant: 'success',
                    });

                    // Update store.
                    dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                })
                .catch((error) => {
                    console.error(error);
                    const errorMessage = error.response?.data?.exception?.message;
                    enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                        variant: 'error',
                    });
                });
        };
        updateWorkGroup();
    };

    return (
        <div
            style={{
                width: historicInscription ? '25vw' : '50vw',
                height: '70vh',
            }}
        >
            {/* Header section */}
            <Typography
                display="flex"
                alignItems="center"
                width="100%"
                color={theme.palette.secondary.main}
                paddingBottom={1}
                variant="h4"
            >
                Gestion des membres
            </Typography>

            <Divider
                sx={{
                    backgroundColor: theme.palette.secondary.main,
                    borderWidth: '1px',
                }}
            />

            {/* Body section */}

            <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
            >
                {/* User search container */}
                {isActiveStatus && !historicInscription && (
                    <Box
                        style={{
                            width: '48%',
                        }}
                    >
                        <SearchBar
                            placeholder="Rechercher un utilisateur par nom, prénom, mail..."
                            onChange={(e) => {
                                setSearchBarValue(e.target.value);
                                delayedSearch(e.target.value);
                            }}
                            value={searchBarValue}
                        />

                        <Box
                            data-testid="applying-members-list-container"
                            style={{
                                height: '320px',
                                margin: '16px 0px',
                            }}
                        >
                            {searchDataSource.status === 'loading' && (
                                <CenterCircularProgress sx={{ height: '100%' }} />
                            )}

                            {searchDataSource.data &&
                                searchBarValue !== '' &&
                                searchDataSource.status === 'succeeded' &&
                                searchDataSource.data.map(
                                    (user) =>
                                        checkCandidatesId(user.id as number) && (
                                            <SearchMembersWidget
                                                key={user.id}
                                                candidate={user}
                                                setCandidate={setSelectedCandidate}
                                                sx={{
                                                    ':hover': {
                                                        backgroundColor: theme.palette.grey.A700,
                                                    },
                                                }}
                                            />
                                        ),
                                )}
                        </Box>

                        <Typography
                            variant="l2dh7"
                            sx={{ lineHeight: '1.4' }}
                        >
                            Nouveau membre :
                        </Typography>

                        <Typography
                            variant="l2dbodyxs"
                            color={theme.palette.text.secondary}
                        >
                            Cliquer sur un utilisateur pour le sélectionner
                        </Typography>

                        <Box
                            data-testid="selected-applier-container"
                            sx={{
                                marginTop: '12px',
                                width: '100%',
                                height: '51px',
                            }}
                        >
                            {selectedCandidate ? (
                                <SearchMembersWidget
                                    key={selectedCandidate.id}
                                    candidate={selectedCandidate}
                                />
                            ) : (
                                ''
                            )}
                        </Box>
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="flex-end"
                        >
                            <Button
                                type="button"
                                variant="contained"
                                style={{
                                    marginTop: '16px',
                                }}
                                onClick={updateMembers}
                            >
                                Ajouter au groupe
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Member edit container */}
                <Box
                    style={{
                        width: historicInscription ? '100%' : '48%',
                    }}
                >
                    <Typography
                        variant="l2dh7"
                        sx={{ lineHeight: '1.4', marginTop: '8px' }}
                    >
                        {historicInscription ? 'Membres inactifs' : ' Membres actifs'}
                    </Typography>

                    <Typography
                        variant="l2dbodyxs"
                        color={theme.palette.text.secondary}
                    >
                        Cliquer sur l&apos;icône pour accèder au menu de modification
                    </Typography>

                    {!historicInscription ? (
                        // Use Active inscriptions from store here
                        <Box
                            style={{
                                margin: '16px 0px',
                            }}
                        >
                            {inscriptionsDataSource.status === 'loading' && (
                                <CenterCircularProgress sx={{ height: '100%', margin: 'auto' }} />
                            )}

                            {inscriptionsDataSource.data &&
                                inscriptionsDataSource.status === 'succeeded' &&
                                inscriptionsDataSource.data.map((user) => (
                                    <EditMembersWidget
                                        key={user.id}
                                        member={user}
                                        workgroup={workgroup}
                                        historicInscription={historicInscription}
                                    />
                                ))}
                        </Box>
                    ) : (
                        // Use members here depending on historic inscription = true.
                        <Box
                            style={{
                                margin: '16px 0px',
                            }}
                        >
                            {members &&
                                members.map((user) => (
                                    <EditMembersWidget
                                        key={user.id}
                                        member={user}
                                        workgroup={workgroup}
                                        historicInscription={historicInscription}
                                    />
                                ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </div>
    );
};

export default WorkGroupUpdateMembersForm;
